import React from 'react';

import {
  fetchLeave,
  FORM_DATA_KEY_FACTORY,
  useFormDataQuery,
  useOffers,
} from '@/api/getters';
import {
  useFormMutation,
  useOfferAcceptedMutation,
  useRejectAllOffersMutation,
  useSubmitStepMutation,
} from '@/api/setters';
import { Steps } from '@/api/types';
import IconCheck from '@/assets/check.svg?react';
import IconFolder from '@/assets/folder.svg?react';
import IconInfo from '@/assets/info-shape.svg?react';
import IconProfit from '@/assets/profit.svg?react';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { PRODUCT } from '@/consts';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';
import OfferLoading from '@/features/v2/summaryForms/components/offer/OfferLoading';
import { queryClient } from '@/lib/query';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useParams } from 'react-router-dom';

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  maximumFractionDigits: 0,
  currency: 'PLN',
});

const Offers = () => {
  const { id } = useParams();
  const { data: offersData, isLoading } = useOffers();
  const { data } = useFormDataQuery();
  const { setStep, setStepTag, stepTag, innerObject, step } = useStepper();
  const { i18n } = useLingui();
  const submitStep = useSubmitStepMutation();

  const offerLength = offersData?.offers.length;
  const costsLength = offersData?.costs.length;

  const isHouseHold = data?.product.id === PRODUCT.HOUSE_HOLD;

  const acceptOfferMutation = useOfferAcceptedMutation({ isHouseHold });
  const acceptOfferMutation1 = useOfferAcceptedMutation({ isHouseHold });
  const acceptOfferMutation2 = useOfferAcceptedMutation({ isHouseHold });

  const rejectOfferMutation = useRejectAllOffersMutation({ isHouseHold });
  const formMutation = useFormMutation();

  if (isLoading && !offersData) return <OfferLoading />;

  const handleAcceptOffer = async (hash: string, index: number) => {
    if (!data) return;
    await formMutation.mutateAsync({
      ...formMutationDto({
        form_data: data?.payload?.data ?? null,
        stepper: {
          step: data?.offer.is_complementary ? 4 : 5,
          innerStep: { ...innerObject, [step + 1]: 0 },
        },
      }),
    });
    const acceptMutation =
      index === 0
        ? acceptOfferMutation
        : index === 1
          ? acceptOfferMutation1
          : acceptOfferMutation2;

    await acceptMutation.mutateAsync(hash, {
      onSuccess: async () => {
        setStep(data.offer.is_complementary ? 4 : 5);
        queryClient.invalidateQueries({
          queryKey: FORM_DATA_KEY_FACTORY(hash),
        });
        if (stepTag === Steps.OFFER && !data.offer.is_complementary) {
          submitStep.mutateAsync(Steps.TRANSFER_DETAILS).then(() => {
            setStepTag(Steps.TRANSFER_DETAILS);
          });
        }
        if (data.offer.is_complementary) {
          submitStep.mutateAsync(Steps.COMPLEMENTARY_FORM).then(() => {
            setStepTag(Steps.COMPLEMENTARY_FORM);
          });
        }
        await fetchLeave(id as string);
      },
    });
  };

  const handleRejectOffer = async () => {
    await rejectOfferMutation.mutateAsync();
    await fetchLeave(id as string);
  };

  return (
    <div className="space-y-4 pt-4 lg:space-y-5 lg:pt-0">
      <Paper className="lg:gap-[30px] lg:p-10 lg:pt-[46px]">
        <div className="flex flex-col gap-5 lg:gap-[22px]">
          <Title
            icon={
              <IconFolder className="size-5 items-center justify-center text-primary lg:size-[26px]" />
            }
          >
            {i18n.t('Wybierz rozwiązanie idealne dla siebie')}
          </Title>
          <Carousel>
            {offersData?.offers.map((offer, index) => {
              return (
                <Card
                  className={cn(
                    'min-w-[216px] snap-center',
                    offerLength === 1 && 'w-full lg:w-[442px]',
                    offerLength === 2 && 'lg:w-1/2',
                    offerLength === 3 && 'lg:w-1/3'
                  )}
                  key={offer.hash}
                >
                  <Card.Title>{offer.title}</Card.Title>
                  <Card.Info label={i18n.t('Prognoza wartości zaniżenia')}>
                    {offer.forecastValuePLN
                      ? currencyFormatter.format(offer.forecastValuePLN)
                      : undefined}
                  </Card.Info>
                  <Card.Info
                    label={
                      <>
                        {i18n.t('Kwota netto odkupu')}
                        <sup>1</sup>
                      </>
                    }
                  >
                    {offer.netPurchasePLN
                      ? currencyFormatter.format(offer.netPurchasePLN)
                      : undefined}
                  </Card.Info>
                  <Card.Info label={i18n.t('Wynagrodzenie Helpfind')}>
                    {offer.helpfindFeePercent
                      ? `${offer.helpfindFeePercent}%`
                      : undefined}
                  </Card.Info>
                  <Card.Info
                    last
                    label={
                      <>
                        {i18n.t('Termin wypłaty środków')}
                        <sup>2</sup>
                      </>
                    }
                  >
                    {offer.payoutTerm ?? '—'}
                  </Card.Info>
                  <Card.Footer>
                    <ChooseButton
                      dataTestId={`choose-offer-${index + 1}`}
                      isLoading={
                        index === 0
                          ? acceptOfferMutation.isPending
                          : index === 1
                            ? acceptOfferMutation1.isPending
                            : acceptOfferMutation2.isPending
                      }
                      onClick={() => handleAcceptOffer(offer.hash, index)}
                    />
                  </Card.Footer>
                </Card>
              );
            })}
          </Carousel>
          <Button
            size="small"
            className="max-w-fit"
            onClick={handleRejectOffer}
            loading={rejectOfferMutation.isPending}
            data-testid="reject-offer"
          >
            {i18n.t('Porozmawiajmy o warunkach')}
            {rejectOfferMutation.isPending ? <ButtonLoading /> : null}
          </Button>
        </div>
        <div className="flex flex-col gap-5 lg:gap-[22px]">
          <Title
            icon={
              <IconProfit className="size-5 items-center justify-center text-primary lg:size-[26px]" />
            }
          >
            {i18n.t('Poznaj koszty dostępnych opcji')}
          </Title>
          <Carousel>
            {offersData?.costs.map((cost) => {
              return (
                <Card
                  className={cn(
                    'min-w-[216px] snap-center',
                    costsLength === 1 && 'w-full lg:w-[442px]',
                    costsLength === 2 && 'lg:w-1/2',
                    costsLength === 3 && 'lg:w-1/3'
                  )}
                  key={cost.hash}
                >
                  <Card.Title>{cost.title}</Card.Title>
                  <Card.Info
                    label={
                      <>
                        {i18n.t('Opłata wstępna')}
                        <sup>3</sup>
                      </>
                    }
                  >
                    {cost.initialFeePLN
                      ? currencyFormatter.format(cost.initialFeePLN)
                      : undefined}
                  </Card.Info>
                  <Card.Info label={<>{i18n.t('Wpis sądowy')}</>}>
                    {cost.courtFeePLN
                      ? currencyFormatter.format(cost.courtFeePLN)
                      : undefined}
                  </Card.Info>
                  <Card.Info label={i18n.t('Zastępstwo procesowe')}>
                    {cost.proceduralRepresentationPLN
                      ? currencyFormatter.format(
                          cost.proceduralRepresentationPLN
                        )
                      : undefined}
                  </Card.Info>
                  <Card.Info
                    label={
                      <>
                        {i18n.t('Opinia biegłego')}
                        <sup>4</sup>
                      </>
                    }
                  >
                    {cost.expertOpinionPLN}
                  </Card.Info>
                  <Card.Info label={<>{i18n.t('Koszt pełnomocnictwa')}</>}>
                    {cost.attorneyCostPLN
                      ? currencyFormatter.format(Number(cost.attorneyCostPLN))
                      : undefined}
                  </Card.Info>
                  <Card.Info label={<>{i18n.t('Ryzyko przegrania sprawy')}</>}>
                    {cost.riskBearer ?? '—'}
                  </Card.Info>
                </Card>
              );
            })}
          </Carousel>
        </div>
        <p className="body-small text-dark-gray-500">
          {i18n.t(
            'Niniejsza propozycja nie stanowi oferty w rozumieniu art. 66 Kodeksu cywilnego. Jej charakter jest wyłącznie informacyjny i nie tworzy żadnych zobowiązań prawnych po stronie autora lub podmiotu, który go przygotował. Wszelkie przedstawione informacje, dane lub warunki, w tym wskazane kwoty, opierają się na informacjach przekazanych przez klienta i mogą ulec zmianie, w szczególności jeśli okoliczności sprawy nie będą w pełni odpowiadać danym wskazanym przez klienta. W celu uzyskania wiążącej oferty prosimy o kontakt.'
          )}
        </p>
      </Paper>
      <Paper className="gap-y-5 p-4 pt-6 lg:gap-y-[22px] lg:p-10 lg:pt-[46px]">
        <div className="flex flex-col gap-y-4">
          <Title
            icon={
              <IconInfo className="size-5 items-center justify-center text-primary lg:size-[26px]" />
            }
          >
            {i18n.t('Opisy dostępnych wariantów')}
          </Title>
          <div className="body-small flex flex-col gap-y-2.5">
            {offersData?.offers.some(
              (offer) => offer.title === 'Sprzedaż Twojego odszkodowania'
            ) && (
              <InfoCard>
                <h4 className="h4 text-blue-600">
                  {i18n.t(
                    'Sprzedaż Twojego odszkodowania (cesja wierzytelności)'
                  )}
                </h4>
                <p className="text-dark-gray-500">
                  {i18n.t(
                    'W tym modelu Klient sprzedaje na rzecz Helpfind swoje roszczenie z tytułu szkody. Oznacza to, że'
                  )}{' '}
                  <span className="font-semibold">
                    {i18n.t(
                      'prawa i obowiązki wynikające z tego roszczenia przechodzą na Helpfind'
                    )}
                  </span>
                  {i18n.t(
                    ', a Klient otrzymuje ustaloną kwotę – cenę sprzedaży – niezależnie od tego ile finalnie uda się odzyskać Helpfind na drodze sądowej.'
                  )}
                </p>
                <ul className="mt-0.5 list-inside list-none space-y-0.5 text-dark-gray-500">
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Cena sprzedaży wierzytelności ma charakter wyczerpujący – w pełni zaspokaja roszczenie Klienta względem Helpfind.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Wszystkie środki odzyskane po dacie podpisania umowy należą wyłącznie do Helpfind.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Helpfind odpowiada za dalsze działania (skierowanie sprawy do sądu, pokrycie wszelkich kosztów).'
                    )}
                  </li>
                </ul>
              </InfoCard>
            )}
            {offersData?.offers.some(
              (offer) => offer.title === 'Wsparcie sądowe'
            ) && (
              <InfoCard>
                <h4 className="h4 text-blue-600">
                  {i18n.t('Wsparcie w sądzie (umowa dochodzenia roszczeń)')}
                </h4>
                <p className="text-dark-gray-500">
                  {i18n.t('W tym wariancie')}{' '}
                  {i18n.t('Klient zachowuje swoje roszczenie')}
                  {i18n.t('i zleca Helpfind dochodzenie w swoim imieniu.')}
                </p>
                <ul className="mt-0.5 list-inside list-none space-y-0.5 text-dark-gray-500">
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Klient pozostaje właścicielem roszczenia i stroną w postępowaniu.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Helpfind podejmuje wszelkie niezbędne kroki prawne w imieniu Klienta, w tym reprezentację w sądzie.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Koszty postępowania sądowego (wpisy, opłaty, zaliczki na biegłych, obsługa prawna) pokrywa Klient.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Ryzyko finansowe związane z ewentualną przegraną sprawą również pozostaje po stronie Klienta.'
                    )}
                  </li>
                </ul>
              </InfoCard>
            )}
            {offersData?.offers.some(
              (offer) =>
                offer.title === 'Wsparcie sądowe ze wsparciem finansowym'
            ) && (
              <InfoCard>
                <h4 className="h4 text-blue-600">
                  {i18n.t(
                    'Wsparcie w sądzie (umowa dochodzenia roszczeń z pełnym finansowaniem)'
                  )}
                </h4>
                <p className="text-dark-gray-500">
                  {i18n.t(
                    'Jest to umowa dochodzenia roszczeń z pełnym finansowaniem sprawy przez Helpfind.'
                  )}{' '}
                  <span className="font-semibold">
                    {i18n.t(
                      'Klient zachowuje swoje roszczenie i jest reprezentowany przez Helpfind'
                    )}
                  </span>
                  {i18n.t(', ale nie ponosi kosztów postępowania.')}
                </p>
                <ul className="mt-0.5 flex list-inside list-none flex-col space-y-0.5 text-dark-gray-500">
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Helpfind pokrywa wszystkie opłaty związane ze sprawą.'
                    )}
                  </li>
                  <li className="inline-flex items-start">
                    <IconCheck className="mr-1 size-[15px] lg:size-4" />
                    {i18n.t(
                      'Szczegółowe warunki finansowania dochodzenia roszczenia opisane są w umowie.'
                    )}
                  </li>
                </ul>
              </InfoCard>
            )}
          </div>
          <InfoCard className="gap-2.5">
            <div className="flex flex-col gap-0.5 text-dark-gray-500">
              <h4 className="font-jakarta text-xs font-medium leading-4 text-blue-600">
                {i18n.t('Kwota netto odkupu')}
                <sup>1</sup>
              </h4>
              <p className="text-xs">
                {i18n.t(
                  'W przypadku zawarcia umowy cesji wierzytelności poprzez osobę fizyczną Helpfind Funding sp. z o.o. odprowadzi do właściwego Urzędu Skarbowego zaliczkę na podatek dochodowy w określonej ustawowo wartości 12% od proponowanej kwoty brutto.'
                )}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 text-dark-gray-500">
              <h4 className="font-jakarta text-xs font-medium leading-4 text-blue-600">
                {i18n.t('Termin wypłaty środków')}
                <sup>2</sup>
              </h4>
              <p className="text-xs">
                {i18n.t(
                  'Zgodnie z raportem Instytutu Wymiaru Sprawiedliwości - średni czas trwania postępowania przed sądem I instancji od dnia wniesienia pozwu do wydania orzeczenia wynosił 433,67 dnia, a mediana 395. Najkrótsze postępowanie trwało tylko 7 dni, a najdłuższe aż 1981 dni. Wartości te są nieco niższe dla spraw, w których dochodzono wyłącznie roszczeń z tytułu szkody na mieniu. Średni czas trwania postępowania wynosił tu 413,34 dnia, mediana 384 dni, najkrótsze postępowanie trwało 7 dni, a najdłuższe 1276 dni. Ostatecznie zakończona sprawa sądowa jest wyrokiem prawomocnym.'
                )}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 text-dark-gray-500">
              <h4 className="font-jakarta text-xs font-medium leading-4 text-blue-600">
                {i18n.t('Opłata wstępna')}
                <sup>3</sup>
              </h4>
              <p className="text-xs">
                {i18n.t(
                  'Opłata wstępna obejmuje analizę dokumentów dostarczonych przez Klienta, przygotowanie wstępnych pism procesowych lub pozaprocesowych oraz zlecenie przygotowania kosztorysu przez rzeczoznawcę w zakresie uzgodnionym z Klientem. Opłata wstępna stanowi wynagrodzenie za etap przygotowawczy i nie podlega zwrotowi w przypadku rezygnacji Klienta z dalszej współpracy.'
                )}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 text-dark-gray-500">
              <h4 className="font-jakarta text-xs font-medium leading-4 text-blue-600">
                {i18n.t('Opinia biegłego')}
                <sup>4</sup>
              </h4>
              <p className="text-xs">
                {i18n.t(
                  'Zgodnie z raportem Instytutu Wymiaru Sprawiedliwości – w większości badanych spraw (ok. 80%) zaangażowano co najmniej jednego biegłego, przy czym w 3 sprawach było to aż 5 biegłych. Przeciętnie wynagrodzenie biegłych w jednej sprawie wyniosło ok. 1275 zł, a mediana ok. 1107 zł. Najniższe wynagrodzenie to 252 zł, a najwyższe 9340 zł. Konieczność przeprowadzenia dowodu z opinii biegłego jest uznawana za regułę w badanych postępowaniach. Jak się wydaje, konieczności tej trudno jest uniknąć.'
                )}
              </p>
            </div>
          </InfoCard>
        </div>
      </Paper>
    </div>
  );
};

export const InfoCard = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-[10px] bg-gray-200 p-4 lg:rounded-[18px]',
        className
      )}
    >
      {children}
    </div>
  );
};

export const Carousel = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      data-id="carousel"
      className="-mx-4 flex w-[calc(100%+32px)] snap-x snap-mandatory gap-2 overflow-x-auto px-4 scrollbar-none lg:gap-[15px]"
    >
      {children}
    </div>
  );
};

export const ChooseButton = ({
  onClick,
  isLoading,
  dataTestId,
}: {
  onClick?: () => void;
  isLoading?: boolean;
  dataTestId?: string;
}) => {
  const { i18n } = useLingui();
  return (
    <Button
      style={{ width: '100%' }}
      colors="green"
      size={'small'}
      onClick={onClick}
      loading={isLoading}
      data-testid={dataTestId}
    >
      {i18n.t('Wybieram')}
      {isLoading ? (
        <ButtonLoading className="text-dark-gray-300" />
      ) : (
        <ButtonRightArrow />
      )}
    </Button>
  );
};

export const CardFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="mt-1.5 flex w-full flex-col gap-1 lg:gap-2">{children}</div>
  );
};

export const CardInfo = ({
  children,
  label,
  last,
}: {
  children?: React.ReactNode | null;
  label: React.ReactNode;
  last?: boolean;
}) => {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 rounded-md bg-gray-100 px-3 py-1.5 lg:gap-1 lg:rounded-[10px] lg:p-2">
      <p
        className={cn(
          'text-balance text-center font-jakarta text-[13px] font-semibold leading-[18px] tracking-normal text-oxford-blue-500 lg:text-sm lg:font-bold lg:leading-5',
          last && 'flex h-[54px] items-center justify-center lg:h-[38px]'
        )}
      >
        {children || '—'}
      </p>
      <h4 className="text-center font-jakarta text-[10px] font-medium leading-3 text-dark-gray-400 lg:text-[11px] lg:leading-4">
        {label}
      </h4>
    </div>
  );
};

export const CardTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex h-[44px] w-full items-center justify-between px-2 py-1.5 lg:h-[52px] lg:px-[18px] lg:py-1.5">
      <h3 className="w-full text-balance text-center font-jakarta text-[13px] font-semibold leading-[18px] tracking-normal text-oxford-blue-500 lg:text-sm lg:font-bold lg:leading-5">
        {children}
      </h3>
    </div>
  );
};

export const Card = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      data-id="card"
      className={cn(
        'flex flex-col gap-1 rounded-[14px] border-2 border-green-500 bg-gray-200 px-3.5 py-2.5 lg:rounded-[18px] lg:p-3.5',
        className
      )}
    >
      {children}
    </div>
  );
};

Card.Title = CardTitle;
Card.Info = CardInfo;
Card.Footer = CardFooter;

export const Paper = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      data-id="Paper"
      className={cn(
        'flex flex-col gap-5 rounded-lg border-t-[6px] border-t-primary bg-gray-100 p-4 pt-6 lg:gap-[22px] lg:rounded-[10px] lg:border-t-[6px] lg:px-10 lg:pb-[30px] lg:pt-9',
        className
      )}
    >
      {children}
    </div>
  );
};

export const Title = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <div data-id="title" className="flex gap-2.5">
      <div className="flex items-center">{icon}</div>
      <h2 className="lg:h2 h3">{children}</h2>
    </div>
  );
};

export const Cell = ({
  icon,
  label,
  className,
  spanClassName,
  value,
  disableTooltip,
}: {
  icon: React.ReactNode;
  label: React.ReactNode | string;
  className?: string;
  spanClassName?: string;
  value?: string;
  disableTooltip?: boolean;
}) => {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {icon}
      <span className={cn('body-small flex gap-0.5', spanClassName)}>
        {label && <span>{label}</span>}
        {value &&
          (disableTooltip ? (
            <span className="block truncate font-medium lg:max-w-[120px]">
              {value}
            </span>
          ) : (
            <TooltipProvider delayDuration={0} disableHoverableContent>
              <Tooltip disableHoverableContent>
                <TooltipTrigger
                  type="button"
                  className="block truncate font-medium lg:max-w-[120px]"
                >
                  {value}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{value}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
      </span>
    </div>
  );
};

export default Offers;
